import { useId, useState } from "react";

import type { ReportFormValues, ReportUse } from "./types";

import "./ReportModal.css";

const REPORT_USE_OPTIONS: ReportUse[] = ["Commercial", "Academic", "Personal"];

const EMPTY_FORM: ReportFormValues = {
  use: "",
  name: "",
  email: "",
  siteName: "",
  organisation: "",
  acceptedTerms: false,
};

export function ReportModal({
  open,
  status,
  termsUrl,
  showForm,
  isProcessing,
  onCancel,
  onClose,
  onSubmit,
}: {
  open: boolean;
  status: string;
  termsUrl?: string;
  showForm: boolean;
  isProcessing: boolean;
  onCancel: () => void;
  onClose: () => void;
  onSubmit: (values: ReportFormValues) => void;
}) {
  const [values, setValues] = useState<ReportFormValues>(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const useIdValue = useId();
  const siteNameId = useId();
  const nameId = useId();
  const emailId = useId();
  const organisationId = useId();
  const acceptedTermsId = useId();

  if (!open) return null;

  const showFormControls = showForm && !isProcessing;
  const requiresOrganisation =
    values.use === "Commercial" || values.use === "Academic";
  const isPersonalUse = values.use === "Personal";

  const updateUse = (use: ReportUse | "") => {
    setFormError("");
    setValues((current) => ({
      ...current,
      use,
      organisation: use === "Personal" ? "" : current.organisation,
    }));
  };

  const updateTextField = (
    field: "siteName" | "name" | "email" | "organisation",
    value: string,
  ) => {
    setFormError("");
    setValues((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateAcceptedTerms = (acceptedTerms: boolean) => {
    setFormError("");
    setValues((current) => ({
      ...current,
      acceptedTerms,
    }));
  };

  const handleSubmit = () => {
    if (isProcessing) return;

    const trimmed = {
      use: values.use,
      siteName: values.siteName.trim(),
      name: values.name.trim(),
      email: values.email.trim(),
      organisation: values.organisation.trim(),
      acceptedTerms: values.acceptedTerms,
    };

    if (!trimmed.use) {
      setFormError("Select a use.");
      return;
    }

    if (requiresOrganisation && !trimmed.organisation) {
      setFormError("Enter a company or organisation.");
      return;
    }

    if (!trimmed.name || !trimmed.email) {
      setFormError("Fill in all required fields.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed.email)) {
      setFormError("Enter a valid email address.");
      return;
    }

    if (!trimmed.acceptedTerms) {
      setFormError("Accept the Terms and Conditions to continue.");
      return;
    }

    setFormError("");
    onSubmit(trimmed);
  };

  return (
    <div
      className="modal-backdrop"
      onMouseDown={isProcessing ? undefined : onClose}
    >
      <section
        className="modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="report-title">Request Report</h2>
        </div>
        <div className="report-form">
          {showFormControls && (
            <>
              <label className="form-field" htmlFor={useIdValue}>
                <span className="form-label-text">
                  Use <span className="required-mark">*</span>
                </span>
                <select
                  id={useIdValue}
                  name="use"
                  required
                  aria-required="true"
                  value={values.use}
                  onChange={(event) =>
                    updateUse(event.currentTarget.value as ReportUse | "")
                  }
                >
                  <option value="" disabled>
                    Select use
                  </option>
                  {REPORT_USE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label className="form-field" htmlFor={organisationId}>
                <span className="form-label-text">
                  Company/Organisation
                  {requiresOrganisation && (
                    <>
                      {" "}
                      <span className="required-mark">*</span>
                    </>
                  )}
                </span>
                <input
                  id={organisationId}
                  name="organisation"
                  value={values.organisation}
                  autoComplete="organization"
                  disabled={isPersonalUse}
                  required={requiresOrganisation}
                  aria-required={requiresOrganisation}
                  onChange={(event) =>
                    updateTextField("organisation", event.currentTarget.value)
                  }
                />
              </label>
              <label className="form-field" htmlFor={nameId}>
                <span className="form-label-text">
                  Name <span className="required-mark">*</span>
                </span>
                <input
                  id={nameId}
                  name="name"
                  required
                  aria-required="true"
                  value={values.name}
                  autoComplete="name"
                  onChange={(event) =>
                    updateTextField("name", event.currentTarget.value)
                  }
                />
              </label>
              <label className="form-field" htmlFor={emailId}>
                <span className="form-label-text">
                  Email <span className="required-mark">*</span>
                </span>
                <input
                  id={emailId}
                  name="email"
                  type="email"
                  required
                  aria-required="true"
                  value={values.email}
                  autoComplete="email"
                  onChange={(event) =>
                    updateTextField("email", event.currentTarget.value)
                  }
                />
              </label>
              <label className="form-field" htmlFor={siteNameId}>
                <span className="form-label-text">Site Name</span>
                <input
                  id={siteNameId}
                  name="siteName"
                  value={values.siteName}
                  onChange={(event) =>
                    updateTextField("siteName", event.currentTarget.value)
                  }
                />
              </label>
              <div className="form-checkbox-row">
                <input
                  id={acceptedTermsId}
                  name="acceptedTerms"
                  type="checkbox"
                  required
                  aria-required="true"
                  checked={values.acceptedTerms}
                  onChange={(event) =>
                    updateAcceptedTerms(event.currentTarget.checked)
                  }
                />
                <span>
                  <label htmlFor={acceptedTermsId}>
                    By ticking this box, I confirm that I have read and agree to
                    the
                  </label>{" "}
                  {termsUrl ? (
                    <a href={termsUrl} target="_blank" rel="noreferrer">
                      Terms and Conditions
                    </a>
                  ) : (
                    "Terms and Conditions"
                  )}
                  <label htmlFor={acceptedTermsId}>.</label>
                </span>
              </div>
            </>
          )}

          {(formError || status || isProcessing) && (
            <div className="form-status" role="status">
              {isProcessing && <span className="spinner" />}
              {formError || status}
            </div>
          )}

          <div className="modal-actions">
            {isProcessing ? (
              <button
                className="danger-button"
                type="button"
                onClick={onCancel}
              >
                Abort Task
              </button>
            ) : (
              <>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={onClose}
                >
                  {showFormControls ? "Cancel" : "Close"}
                </button>
                {showFormControls && (
                  <button
                    className="primary-button"
                    type="button"
                    onClick={handleSubmit}
                  >
                    Generate report
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
